import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toTitleCase } from "@/utils/titleCase";
import { CountrySelectWithOther } from "@/components/shared";

/**
 * Shared location fields for non-flight transit (From/To + optional country),
 * single-location segments (accommodation/activity: property name + location +
 * country), and the optional Country field for flights.
 *
 * Flight airport pickers (AirportSearchField) are NOT rendered here because
 * Add and Edit have meaningfully different airport-search wiring (Add updates
 * `from_location` while typing; Edit gates on `setHasSearched`). The dialogs
 * keep that small block inline.
 */
export function SegmentLocationFields({
  segment,
  onChange,
  tripCountries,
  testIdPrefix = "segment",
  countryRequired = true,
}) {
  const type = segment.type;
  const showLocationFields = ["flight", "train", "bus", "cruise", "car_rental", "rideshare"].includes(type);
  const isSingleLocation = ["accommodation", "activity"].includes(type);
  const needsCountry = ["accommodation", "activity", "cruise", "train", "bus", "car_rental", "rideshare"].includes(type);
  const star = countryRequired ? " *" : "";

  // Non-flight transit: From/To inputs + optional embarkation country.
  if (showLocationFields && type !== "flight") {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="grid gap-2">
            <Label htmlFor="from">From</Label>
            <Input
              id="from"
              data-testid={`${testIdPrefix}-from-input`}
              placeholder="Departure location"
              value={segment.from_location || ""}
              onChange={(e) => onChange({ from_location: toTitleCase(e.target.value) })}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="to">To</Label>
            <Input
              id="to"
              data-testid={`${testIdPrefix}-to-input`}
              placeholder="Arrival location"
              value={segment.to_location || ""}
              onChange={(e) => onChange({ to_location: toTitleCase(e.target.value) })}
            />
          </div>
        </div>

        {needsCountry && (
          <div className="grid gap-2">
            <Label htmlFor="country">Embarkation Country{star}</Label>
            <CountrySelectWithOther
              value={segment.country}
              onChange={(value) => onChange({ country: value })}
              tripCountries={tripCountries}
              id="country"
              testId={`${testIdPrefix}-country-select`}
            />
          </div>
        )}
      </div>
    );
  }

  // Single-location: accommodation/activity (property name + location + country)
  if (isSingleLocation) {
    return (
      <div className="space-y-4">
        {type === "accommodation" && (
          <div className="grid gap-2">
            <Label htmlFor="property_name">Property Name</Label>
            <Input
              id="property_name"
              data-testid={`${testIdPrefix}-property-name-input`}
              placeholder="e.g., Hilton Garden Inn, The Ritz-Carlton"
              value={segment.property_name || ""}
              onChange={(e) => onChange({ property_name: toTitleCase(e.target.value) })}
            />
          </div>
        )}

        <div className="grid gap-2">
          <Label htmlFor="accommodation_location">
            {type === "activity" ? `Location / Name${star}` : `Location${star}`}
          </Label>
          <Input
            id="accommodation_location"
            data-testid={`${testIdPrefix}-accommodation-location-input`}
            placeholder={type === "accommodation" ? "Hotel address or city" : "Event venue or location"}
            value={segment.accommodation_location || ""}
            onChange={(e) => onChange({ accommodation_location: toTitleCase(e.target.value) })}
          />
        </div>

        <div className="grid gap-2">
          <Label htmlFor="country">Country{star}</Label>
          <CountrySelectWithOther
            value={segment.country}
            onChange={(value) => onChange({ country: value })}
            tripCountries={tripCountries}
            id="country"
            testId={`${testIdPrefix}-country-select`}
          />
        </div>
      </div>
    );
  }

  return null;
}

export default SegmentLocationFields;
